import { useState, useEffect, useRef, RefObject, useMemo, useCallback } from 'react';
import { removeNullFalseEmptyStringKeys } from '../helpers/utils';
import { DistinctFilterResponse } from '../types';

type QueryTypes = {
  type: string;
  statusType: string;
  columnSort?: string;
  sortType?: string;
};

type QueryParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | Record<string, string | number | boolean | undefined>;

type QueryParams = Record<string, QueryParamValue>;

type PaginatedApiResponse<TItem> = DistinctFilterResponse & {
  total: number;
} & Record<string, TItem[] | number | string | boolean | undefined | null | unknown>;

type FetchPageQuery = {
  page: number;
  perPage: number;
} & QueryParams;

const THRESHOLD = 50;

const toQueryParams = (obj: Record<string, unknown>): QueryParams =>
  removeNullFalseEmptyStringKeys(obj) as QueryParams;

export const useInfiniteScroll = <TItem,>(
  types: QueryTypes = { type: '', statusType: '' },
  containerRef: RefObject<HTMLElement>,
  fetchData: (data: FetchPageQuery) => Promise<{ data: PaginatedApiResponse<TItem> }>,
  pageSize = 20,
  queryParams: QueryParams = {},
  dataSetterCallback?: (data: TItem[]) => void,
) => {
  const [combinedData, setCombinedData] = useState<TItem[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const lastQueryData = useRef<QueryParams | null>(null);
  const page = useRef<number>(1);
  const [response, setResponse] = useState<PaginatedApiResponse<TItem> | null>(null);

  const queryData = useMemo<FetchPageQuery>(() => {
    const params: FetchPageQuery = {
      page: page.current,
      perPage: pageSize,
      ...toQueryParams(types),
    };
    return queryParams ? { ...params, ...toQueryParams(queryParams) } : params;
  }, [queryParams, pageSize, types]);

  const hasQueryChanged = JSON.stringify(queryParams) !== JSON.stringify(lastQueryData.current);

  const setData = useCallback(
    (data: TItem[] | ((prevData: TItem[]) => TItem[])) => {
      if (typeof data === 'function') {
        setCombinedData((prevData) => {
          const nextData = data(prevData);
          dataSetterCallback?.(nextData);
          return nextData;
        });
      } else {
        dataSetterCallback?.(data);
        setCombinedData(data);
      }
    },
    [dataSetterCallback],
  );

  const fetchPageData = useCallback(
    async (queryVar: FetchPageQuery = queryData, lastPage = isLastPage) => {
      if (isDataLoading || !fetchData || lastPage) return;
      setIsDataLoading(true);
      try {
        lastQueryData.current = queryParams;
        const { data } = await fetchData(queryVar);
        setResponse(data);
        const key = Object.keys(data).find((entryKey) => Array.isArray(data[entryKey]));
        if (key && Array.isArray(data[key])) {
          const pageItems = data[key] as TItem[];
          if (queryVar.page === 1) {
            setIsLastPage(pageItems.length === data.total);
            setData(pageItems);
          } else {
            setIsLastPage(pageItems.length + combinedData.length === data.total);
            setData((prevData) => [...prevData, ...pageItems]);
          }
        }
        page.current = queryVar.page;
      } catch (error) {
        console.error(error);
      } finally {
        setIsDataLoading(false);
      }
    },
    [combinedData.length, fetchData, isDataLoading, isLastPage, queryData, queryParams, setData],
  );

  const refreshData = useCallback(() => {
    setIsLastPage(false);
    page.current = 1;
    fetchPageData({ ...queryData, perPage: pageSize, page: page.current }, false);
  }, [fetchPageData, pageSize, queryData]);

  useEffect(() => {
    const container = containerRef.current;
    const handleScroll = async () => {
      if (!container || isDataLoading || isLastPage) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - THRESHOLD && lastQueryData.current) {
        await fetchPageData({ ...queryData, page: page.current + 1 });
      }
    };
    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [containerRef, fetchPageData, isDataLoading, isLastPage, queryData]);

  useEffect(() => {
    if (hasQueryChanged) refreshData();
  }, [hasQueryChanged, queryParams, refreshData]);

  return { data: combinedData, isDataLoading, fetchPageData, refreshData, response };
};
